/*
 * This file contains shared constants and utilities related to DQL, such as
 * for the DQLEditor and for the CodeSnippet's PrismJS syntax highlighting definition for DQL.
 */

/**
 * Taken from the latest DQL release in the artifactory
 * Exported only for testing purposes.
 */
export const dqlCommands = [
  'aggregate',
  'append',
  'data',
  'dedup',
  'describe',
  'execute',
  'expand',
  'experimentalSave',
  'experimentalTimeChart',
  'failIf',
  'fetch',
  'fields',
  'fieldsAdd',
  'fieldsFlatten',
  'fieldsKeep',
  'fieldsMove',
  'fieldsRemove',
  'fieldsRename',
  'fieldsSnapshot',
  'fieldsSummary',
  'filter',
  'filterOut',
  'group',
  'internalTimeseriesBins',
  'join',
  'joinNested',
  'jsonExtract',
  'limit',
  'load',
  'lookup',
  'makeTimeseries',
  'metrics',
  'parse',
  'previewExternalList',
  'previewExternalParse',
  'search',
  'smartscapeEdges',
  'smartscapeNodes',
  'sort',
  'summarize',
  'timeseries',
  'traverse',
];

// The commands need to be sorted by length. Longer names should be at the start of the list
// If `fields` comes before `fieldsAdd` in the array we would match `fieldsAdd` as `fields` without `Add`
// We cannot rely on a empty space after a command either. `fetch/* comment */logs` should match `fetch`
export const sortedDqlCommands = dqlCommands.toSorted(
  (a, b) => b.length - a.length,
);

/**
 * Matches a parameter name in DQL, which is defined as a word followed by a colon.
 * For example, in `scanLimitGb: 1`, `scanLimitGb` would be matched as a parameter name.
 * It does not include the colon in the match, so only the parameter name is highlighted.
 */
export const parameterNamePattern = /\w+\s*(?=:)/;

/**
 * Matches boolean literals in DQL, which are `true`, `false`, `TRUE`, and `FALSE`.
 * The word boundaries ensure that we only match standalone boolean literals, and not parts
 * of other words (e.g., it won't match the 'true' in 'trueValue').
 */
export const booleanLiteralPattern = /\b(?:true|false)\b/i;

/**
 * Matches null literals in DQL, case-insensitively.
 * The word boundaries ensure that we only match standalone null literals, and not parts
 * of other words (e.g., it won't match the 'null' in 'nullCount' or 'isNull').
 */
export const nullLiteralPattern = /\bnull\b/i;

/**
 * Duration unit suffixes for DQL numeric literals.
 */
const dqlDurationUnits = [
  'minutes',
  'minute',
  'hours',
  'hour',
  'mins',
  'secs',
  'hrs',
  'min',
  'sec',
  'ms',
  'ns',
  'us',
  'hr',
  'h',
  's',
  'm',
];

/**
 * Calendar unit suffixes for DQL numeric literals.
 */
const dqlCalendarDurationUnits = [
  'quarters',
  'quarter',
  'months',
  'weeks',
  'years',
  'month',
  'qtrs',
  'week',
  'year',
  'days',
  'day',
  'qtr',
  'yrs',
  'yr',
  'M',
  'w',
  'd',
  'q',
  'y',
];

export const combinedDurationUnits = [
  ...dqlDurationUnits,
  ...dqlCalendarDurationUnits,
];

/**
 * Regex pattern to match an optional duration or calendar unit suffix in DQL numeric literals.
 */
export const dqlUnitSuffixPattern = `(?:${combinedDurationUnits.join('|')})`;
